/**
 * The composer at the foot of a ticket.
 *
 * One box that writes two different things, which is why the internal toggle is
 * a pressed pill rather than a checkbox in the corner: the difference between a
 * reply the client reads and a note only the office reads is the single most
 * expensive mistake this screen can let somebody make, so the box itself changes
 * - the rule down its side, the placeholder and the send button's word all move
 * with it.
 *
 * The AI draft lands in the same textarea rather than in a panel of its own. It
 * is a draft, not an answer, and putting it anywhere else invites sending it
 * unread.
 *
 * Files use the mailbox's own tray (`_shared/attachments`), unchanged. A ticket
 * reply leaves the building as e-mail, so "what may be attached" is the same
 * question with the same answer, and a second tray beside it would be a second
 * set of limits to keep in step. An internal note may carry files too: it is
 * not sent, so neither are they, which is exactly how a screenshot of a client's
 * broken checkout gets parked on the ticket without being handed back to them.
 */
import { useRef, useState } from "react";
import { Loader2, Lock, Send, Sparkles } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { postTicketMessage, suggestReply, type CannedReply } from "../../../lib/support";
import { Btn, MenuField } from "../../ui";
import { AttachButton, AttachmentTray, useComposerAttachments } from "../_shared/attachments";

export function ReplyBox({
  ticketId,
  canned,
  siteId,
  onSent,
}: {
  ticketId: string;
  canned: CannedReply[];
  /** What the media picker needs. Absent for an account with no host site. */
  siteId?: string | null;
  onSent: () => void;
}) {
  const t = useT();
  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const tray = useComposerAttachments();

  async function send() {
    if (!body.trim()) return;
    setSending(true);
    try {
      await postTicketMessage(ticketId, {
        body: body.trim(),
        internal,
        ...(tray.ready.length ? { attachmentIds: tray.ready.map((f) => f.id) } : {}),
      });
      setBody("");
      setInternal(false);
      // `clear`, not `discard`: the message has taken these files and the rows
      // behind them are spent. Discarding would ask the server to delete the
      // uploads the reply that just went out is made of.
      tray.clear();
      onSent();
    } finally {
      setSending(false);
    }
  }

  async function draft() {
    setAiBusy(true);
    setAiError(null);
    try {
      const r = await suggestReply(ticketId);
      setBody(r.text);
      ref.current?.focus();
    } catch (e) {
      // 503 is the one failure the operator can act on: nobody has configured a
      // provider yet. Everything else is "try again", and saying so is honest.
      const status = (e as { status?: number })?.status;
      setAiError(status === 503 ? t("support.aiNotConfigured") : t("support.aiError"));
    } finally {
      setAiBusy(false);
    }
  }

  function insertCanned(id: string) {
    const c = canned.find((x) => x.id === id);
    if (!c) return;
    // Appended, not replaced: a saved reply is usually the middle of an answer
    // somebody has already started typing.
    setBody((prev) => (prev ? `${prev}\n\n${c.body}` : c.body));
    ref.current?.focus();
  }

  return (
    <div
      className={cn(
        "rounded-[24px] bg-[var(--crm-sunk)] p-4",
        internal && "shadow-[inset_3px_0_0_0_color-mix(in_oklab,var(--color-warning)_60%,transparent)]",
      )}
    >
      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        {canned.length > 0 ? (
          <MenuField
            value=""
            onChange={insertCanned}
            label={t("support.insertCanned")}
            options={[
              { value: "", label: t("support.insertCanned") },
              ...canned.map((c) => ({ value: c.id, label: c.title })),
            ]}
            className="max-w-[15rem]"
          />
        ) : null}

        <button type="button" className="crm-cb crm-cb-wide" onClick={() => void draft()} disabled={aiBusy}>
          {aiBusy ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
          {t("support.aiSuggest")}
        </button>

        <AttachButton tray={tray} siteId={siteId} disabled={sending} />

        <span className="crm-push">
          <button
            type="button"
            aria-pressed={internal}
            onClick={() => setInternal((v) => !v)}
            className={cn("crm-cb crm-cb-wide", internal && "is-armed")}
          >
            <Lock className="size-4" />
            {t("support.internalNote")}
          </button>
        </span>
      </div>

      {aiError ? <p className="mb-2 text-[12.5px] text-[var(--color-destructive)]">{aiError}</p> : null}

      <textarea
        ref={ref}
        className="crm-area"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        placeholder={internal ? t("support.internalPlaceholder") : t("support.replyPlaceholder")}
      />

      <AttachmentTray tray={tray} />

      <div className="mt-3 flex justify-end">
        <Btn
          tone={internal ? "dark" : "brand"}
          onClick={() => void send()}
          // Send waits for the uploads. A reply that went out while a file was
          // still going up would be recorded as carrying it and would not.
          disabled={sending || !body.trim() || tray.busy}
          title={tray.busy ? t("compose.attachWait") : undefined}
        >
          {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          {internal ? t("support.addNote") : t("support.sendReply")}
        </Btn>
      </div>
    </div>
  );
}
